import axios, { type AxiosError } from 'axios'
import {
    useQuery,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query'
import { useAtomValue } from "jotai"

import {
    type FormMutationOptions,
    type MutationOptions,
    type YearlyFeeDiscountFormInput,
    type YearlyFeeDiscount,
    DiscountType,
    type YearlyFeeDiscountAssignInMunicipalityResponse,
} from '@/types'
import { selectedMunicipalityIdAtom } from "@/state"
import { convertToCents, isErrorResponse } from '@/helpers'

export function useYearlyFeeDiscounts(
    municipalityId: number,
    year: number | string,
) {
    return useQuery({
        queryKey: ['yearlyFeeDiscounts', municipalityId, year],
        queryFn: async () => {
            const { data } : { data: Array<YearlyFeeDiscount>} = await axios.get(
                `/api/discount/list?municipality_id=${municipalityId}&year=${year}`
            )
            return data
        },
    })
}

export function useYearlyFeeDiscount(yearlyFeeDiscountId) {
    return useQuery({
        queryKey: ['yearlyFeeDiscount', yearlyFeeDiscountId],
        queryFn: async () => {
            const { data } : { data: YearlyFeeDiscount } = await axios.get(
                '/api/discount/single?id=' + yearlyFeeDiscountId,
            )
            return data
        },
    })
}

export const useCreateYearlyFeeDiscount = (year: number | string, options: FormMutationOptions) => {
    const queryClient = useQueryClient()

    const municipalityId = useAtomValue(selectedMunicipalityIdAtom)

    const {
        onSuccessCallback,
        formDataId,
        setRequestInProgress,
        onErrorCallback,
    } = options

    return useMutation({
        mutationFn: (data: YearlyFeeDiscountFormInput) => {            
            const queryParams = new URLSearchParams()
            queryParams.append('municipality_id', municipalityId.toString())
            queryParams.append('year', year.toString())
            queryParams.append('discount_type', data.discount_type)
            queryParams.append('name', data.name)
            queryParams.append('description', data.description)
            queryParams.append('set_amount_to_zero', data.set_amount_to_zero ? 'true' : 'false')
            queryParams.append('amount_cents', !data.set_amount_to_zero ? convertToCents(data.amount).toString() : '')
            if (data.discount_type === DiscountType.age) {
                queryParams.append('age_from', data.age_from.toString())
                queryParams.append('age_to', data.age_to.toString())
            }

            return axios.post(`/api/discount/create?${queryParams.toString()}`)
        },
        onSuccess: response => {
            queryClient.invalidateQueries({ queryKey: ['yearlyFeeDiscounts'] })
            localStorage.removeItem(formDataId)
            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.');
                }
            }
        }
    })
}

export const useUpdateYearlyFeeDiscount = (yearlyFeeDiscountId, options: FormMutationOptions) => {
    const queryClient = useQueryClient()

    const {
        onSuccessCallback,
        formDataId,
        setRequestInProgress,
        onErrorCallback,
    } = options

    return useMutation({
        mutationFn: (data: YearlyFeeDiscountFormInput) => {
            const queryParams = new URLSearchParams()
            queryParams.append('id', yearlyFeeDiscountId.toString())
            queryParams.append('discount_type', data.discount_type)
            queryParams.append('name', data.name)
            queryParams.append('description', data.description)
            queryParams.append('set_amount_to_zero', data.set_amount_to_zero ? 'true' : 'false')
            queryParams.append('amount_cents', !data.set_amount_to_zero ? convertToCents(data.amount).toString() : '')
            if (data.discount_type === DiscountType.age) {
                queryParams.append('age_from', data.age_from.toString())
                queryParams.append('age_to', data.age_to.toString())
            }

            return axios.put(`/api/discount/update?${queryParams.toString()}`)
        },
        onSuccess: response => {
            queryClient.invalidateQueries({ queryKey: ['yearlyFeeDiscounts'] })
            queryClient.invalidateQueries({ queryKey: ['yearlyFeeDiscount-' + yearlyFeeDiscountId] })
            localStorage.removeItem(formDataId)
            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.');
                }
            }
        }
    })
}

export const useDeleteYearlyFeeDiscount = (yearlyFeeDiscountId: number, municipalityId: number, options: MutationOptions,) => {
    const queryClient = useQueryClient()

    const {
        onSuccessCallback,
        setRequestInProgress,
        onErrorCallback,
    } = options

    return useMutation({
        mutationFn: () => {
            return axios.delete(`/api/discount/delete?id=${yearlyFeeDiscountId}`)
        },
        onSuccess: response => {
            queryClient.invalidateQueries({ queryKey: ['yearlyFeeDiscounts', municipalityId] })
            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.');
                }
            }
        }
    })
}

export const useYearlyFeeDiscountAssignInMunicipality = (options: MutationOptions) => {

    const municipalityId = useAtomValue(selectedMunicipalityIdAtom)

    const {
        setRequestInProgress,
        onErrorCallback,
        onSuccessCallback,
    } = options

    return useMutation({
        mutationFn: (yearlyFeeDiscountId: number) => {
            return axios.post(`/api/discount/assign_in_municipality?id=${yearlyFeeDiscountId
                }&municipality_id=${municipalityId}`)
        },
        onSuccess: response => {
            if (onSuccessCallback) {
                onSuccessCallback(response.data as YearlyFeeDiscountAssignInMunicipalityResponse)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.');
                }
            }
        }
    })
}

export function useYearlyFeeDiscountAssignInMunicipalityStatus(jobId, keepRefetching) {
    return useQuery({
        queryKey: ['generateYearlyFeePaymentsStatus', jobId],
        queryFn: async () => {
            const { data } : { data: YearlyFeeDiscountAssignInMunicipalityResponse } = await axios.get(
                `/api/discount/assign_in_municipality_status?discount_assignments_generation_id=${jobId}`,
            )
            return data
        },
        refetchInterval: keepRefetching ? 2000 : undefined,
    })
}
